import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classNames from 'classnames';

import { LoadingSpinner } from 'legacy-packages/ft-cb';
import { recursiveTagDescDelta } from 'legacy-src/utility/tagHelpers';
import ItemTagsTreeRenderer from 'legacy-src/utility/ItemTagsTreeRenderer';
import ItemTagsFlatRenderer from 'legacy-src/utility/ItemTagsFlatRenderer';
import { fetchTags } from 'legacy-src/utility/ItemTags/state/actions';

const { values } = Object;
/*
 * Draws tags
 * */
const Tags = (props) => {
  const {
    expanded,
    subunitLabel,
    loading,
    fetchTags: doFetchTags,
    itemId,
    itemTags = [],
    userSubjects,
    subjectType,
    adminSubjects,
    isAdminOrCoordinator,
    params,
    questionDetailsToogleRationale,
    isFinalExam,
    educationPeriod,
    onLoad,
  } = props;
  const [cachedItemId, setCachedItemId] = useState(itemId);
  const containerElement = useRef(null);

  useEffect(() => {
    if (expanded) {
      const findAdminSubject = () => {
        const subject = parseInt(params.subject, 10);
        return adminSubjects.filter(
          (obj) => subject === parseInt(obj.masterSubjectId, 10),
        );
      };
      const subjects = isAdminOrCoordinator ? findAdminSubject() : userSubjects;
      // PREAP-1689: Question Details tags weren't showing up for pre-ap subject types.
      // PREAP-1746: The `if subjectType` makes them show up for fym and pre-ap and qb.
      const fymSubject = subjects.find(({ type }) => {
        if (subjectType === 'fym') {
          return type === 'fym' || type === 'pre-ap';
        }
        return type === subjectType;
      });

      if (fymSubject) {
        doFetchTags({
          subjectId: fymSubject.initId,
          itemId,
          questionDetailsToogleRationale,
          educationPeriod,
        });
      }
    }
    if (!expanded) {
      questionDetailsToogleRationale(expanded);
    }
  }, [cachedItemId, expanded]);

  useEffect(() => {
    if (!loading && expanded) {
      onLoad(containerElement.current);
    }
  }, [loading, expanded]);

  if (itemId !== cachedItemId) {
    setCachedItemId(itemId);
  }

  if (loading) {
    return (
      <div
        className={classNames('ItemMetadata', {
          'ItemMetadata--small_right_padding': expanded && !isFinalExam,
        })}
      >
        <div
          className="ItemMetadata--focus"
          role="button"
          tabIndex={-1}
          aria-label="Question details panel expanded"
        />
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div
      ref={containerElement}
      data-test-item-tags={1}
      className={classNames('ItemMetadata', {
        'ItemMetadata--small_right_padding': expanded && !isFinalExam,
      })}
    >
      <div
        className="ItemMetadata--focus"
        role="button"
        tabIndex={-1}
        aria-label="Question details panel expanded"
      />
      <div className="ItemMetadata__tags">
        {values(itemTags).map((section) => {
          if (section.tags && section.tags.length === 0) {
            return null;
          }
          // check if you have to Show Full Text button
          let showFullTextButton = false;
          if (section.hierarchy === 'tree') {
            showFullTextButton = recursiveTagDescDelta(section.tags[0]);
          }
          const key = section.header.replace(' ', '_').toLowerCase();

          return (
            <div className="ItemMetadata__section" key={key}>
              {section.hierarchy === 'flat' ? (
                <ItemTagsFlatRenderer
                  section={section}
                  tags={
                    section.tags
                      ? section.tags.map((tag) => ({
                          ...tag,
                          title:
                            tag.title === 'Subunit' ? subunitLabel : tag.title,
                        }))
                      : []
                  }
                  reactKey={key}
                />
              ) : (
                <ItemTagsTreeRenderer
                  section={section}
                  tags={section.tags || []}
                  showFullTextButton={isFinalExam ? showFullTextButton : false}
                  reactKey={key}
                  isFinalExam={isFinalExam}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

Tags.displayName = 'Tags';

Tags.propTypes = {
  /**
   * SubunitLabel used for overriding category subunit label
   * */
  subunitLabel: PropTypes.string.isRequired,
  /**
   * Item tags
   * */
  itemTags: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  /**
   * Saga loading prop
   * */
  loading: PropTypes.bool,
  /**
   * If tags are expanded
   * */
  expanded: PropTypes.bool,
  /**
   * Location based on pathname
   * can be fym or qb - (subject types)
   * */
  subjectType: PropTypes.oneOf(['fym', 'qb']),
  // show/ hide the rationale in the learnosity player
  questionDetailsToogleRationale: PropTypes.func,
  isFinalExam: PropTypes.bool,
};

Tags.defaultProps = {
  itemTags: [],
  loading: false,
  expanded: false,
  subjectType: 'fym',
  questionDetailsToogleRationale: () => {},
  isFinalExam: false,
  onLoad: () => {},
};

const mapStateToProps = (state) => {
  const {
    itemTags,
    userSubjects,
    userData: {
      isAdmin,
      isCoordinator,
      adminSubjects,
      chosenEducationPeriodCode,
    },
  } = state;
  const { loading, data } = itemTags;

  return {
    loading,
    userSubjects,
    adminSubjects,
    isAdminOrCoordinator: isAdmin || isCoordinator,
    itemTags: data,
    educationPeriod: chosenEducationPeriodCode,
  };
};

export default connect(mapStateToProps, {
  fetchTags,
})(Tags);
